import { Link, useLocation } from 'react-router-dom';
import { mobileNavItems } from './Sidebar';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';

import { useSubscription } from '../../hooks/useSubscription';

/**
 * MobileBottomNav - Bottom navigation bar for mobile devices
 * Shows 4 main navigation items plus a "More" menu
 */
const MobileBottomNav = () => {
    const location = useLocation();
    const [showMore, setShowMore] = useState(false);
    const { isOwner, isManager } = useSubscription();

    // Filter main items
    const filteredMainItems = mobileNavItems.filter(item => {
        if (item.path === '/app/employees') {
            return isOwner || isManager;
        }
        return true;
    });

    return (
        <>
            {/* Bottom Navigation Bar */}
            <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-50 safe-area-inset-bottom">
                <div className="flex justify-around items-center py-2 px-2">
                    {filteredMainItems.map((item) => {
                        const isActive = location.pathname === item.path ||
                            (item.path !== '/app' && location.pathname.startsWith(item.path));
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all min-w-[56px] cursor-pointer ${isActive
                                    ? 'text-primary'
                                    : 'text-gray-500'
                                    }`}
                            >
                                <item.icon className={`w-6 h-6 ${isActive ? 'text-primary' : 'text-gray-500'}`} />
                                <span className={`text-[10px] font-medium ${isActive ? 'text-primary' : 'text-gray-500'}`}>
                                    {item.mobileLabel || item.label}
                                </span>
                            </Link>
                        );
                    })}
                    {/* More button */}
                    <button
                        onClick={() => setShowMore(!showMore)}
                        className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-gray-500 min-w-[56px] cursor-pointer"
                    >
                        <MoreHorizontal className="w-6 h-6" />
                        <span className="text-[10px] font-medium">Más</span>
                    </button>
                </div>
            </nav>

            {/* More Menu Overlay */}
            {showMore && (
                <>
                    {/* Backdrop */}
                    <div
                        className="md:hidden fixed inset-0 bg-black/50 z-40"
                        onClick={() => setShowMore(false)}
                    />
                    {/* Menu Panel */}
                    <div className="md:hidden fixed bottom-16 left-4 right-4 bg-white rounded-2xl shadow-xl z-50 p-4 animate-in slide-in-from-bottom-4 duration-200">
                        <div className="grid grid-cols-3 gap-3">
                            <Link
                                to="/app/services"
                                onClick={() => setShowMore(false)}
                                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                                    <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243z" />
                                    </svg>
                                </div>
                                <span className="text-xs font-medium text-gray-700">Servicios</span>
                            </Link>

                            {(isOwner || isManager) && (
                                <>
                                    <Link
                                        to="/app/finances"
                                        onClick={() => setShowMore(false)}
                                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                                    >
                                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <span className="text-xs font-medium text-gray-700">Finanzas</span>
                                    </Link>
                                    <Link
                                        to="/app/inventory"
                                        onClick={() => setShowMore(false)}
                                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                                    >
                                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                                            <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                        </div>
                                        <span className="text-xs font-medium text-gray-700">Inventario</span>
                                    </Link>
                                    <Link
                                        to="/app/settings"
                                        onClick={() => setShowMore(false)}
                                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                                    >
                                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                                            <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                        <span className="text-xs font-medium text-gray-700">Ajustes</span>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default MobileBottomNav;
